import type { BaanDetail } from "@/features/banen/useBanen";
import { PushStatus, type PloegPush } from "./useBeheer";

/** What a tab shows, top to bottom. */
export type Pane = "stats" | "todo" | "namens" | "waarnemers" | "conflicten" | "push" | "ploegen" | "lockBaan";
export type SignalKey = "vbs" | "conflicten" | "waarnemers";

interface Pagina {
  id: string;
  panes: readonly Pane[];
  /** The signal this tab resolves; a todo row on Overzicht points here. */
  signal?: SignalKey;
  manage?: true;
}

/** The tabs of the beheer page, in the order of the wedstrijddag; the chosen one lives in the `sectie` search param. */
const PAGINAS = [
  { id: "overzicht", panes: ["stats", "todo"] },
  { id: "waarnemers", panes: ["waarnemers", "namens"], signal: "waarnemers", manage: true },
  { id: "conflicten", panes: ["conflicten"], signal: "conflicten" },
  { id: "ploegen", panes: ["ploegen"], signal: "vbs" },
  { id: "afronden", panes: ["push", "lockBaan"] },
] as const satisfies readonly Pagina[];

export type Sectie = (typeof PAGINAS)[number]["id"];
export const SECTIES: readonly Sectie[] = PAGINAS.map((p) => p.id);

/** Red asks for an action, amber is worth a look. */
export type Level = "error" | "warning";

export interface Signal {
  count: number;
  level?: Level;
}

const needsAction = (p: PloegPush) =>
  Number(p.status) === PushStatus.Failed ||
  Number(p.status) === PushStatus.Afwijking ||
  p.refused.length > 0 ||
  p.notStored.length > 0;

/** What needs attention per section; data that has not loaded yet counts as nothing. */
export function beheerSignals(
  ploegen: PloegPush[] | undefined,
  conflicten: number | undefined,
  baan: BaanDetail | undefined,
): Record<"vbs" | "conflicten" | "waarnemers", Signal> {
  const positions = baan
    ? [...(Number(baan.algemeenVraagCount) > 0 ? [null] : []), ...baan.functies.map((f) => Number(f.positionId))]
    : [];
  const assigned = new Set(baan?.waarnemers.map((w) => (w.positionId === null ? null : Number(w.positionId))));
  const signal = (count: number, level: Level): Signal => (count > 0 ? { count, level } : { count });

  return {
    vbs: signal(ploegen?.filter(needsAction).length ?? 0, "error"),
    conflicten: signal(conflicten ?? 0, "warning"),
    waarnemers: signal(positions.filter((p) => !assigned.has(p)).length, "warning"),
  };
}

/** The tabs this person sees, in order: waarnemers is for a beheerder only. */
export function beheerSecties(
  signals: ReturnType<typeof beheerSignals>,
  manage: boolean,
): { id: Sectie; panes: readonly Pane[]; key?: SignalKey; signal?: Signal }[] {
  return (PAGINAS as readonly Pagina[])
    .filter((p) => manage || !p.manage)
    .map((p) => ({ id: p.id as Sectie, panes: p.panes, key: p.signal, signal: p.signal && signals[p.signal] }));
}
